import React, { useEffect, useState } from "react";
import { Content } from "@worcket/common/components/layout";
import { connect, useDispatch } from "react-redux";
import { compose } from "recompose";
import { useTranslation } from "react-i18next";
import * as R from "ramda";
import { List, Avatar, Icon, Spin } from "antd";
import { Route } from "react-router-dom";

import * as gtag from "../../lib/gtag";
import { httpToHttps } from "../../utils/images";
import { fetchCompanies, FETCH_COMPANIES } from "../../store/actions/companies";
import { selectCompany } from "../../store/actions/company";
import Header from "../../components/Header";
import Filters from "../../components/transforms/Filters";
import Placeholder from "../../components/Placeholder";
import Pagination from "../../components/transforms/Pagination";
import Page from "../../components/display/Page";
import Button from "../../components/Button";
import { isLoading } from "../../utils/actions";

import CompanyModal from "./CompanyModal";
import UniversityOrCompanyModal from "./UniversityOrCompanyModal";
import SkeletonCompanies from "./SkeletonCompanies";

import styled from "styled-components/macro";

const CompanyName = styled.p`
  display: inline-flex;
  cursor: pointer;
  margin: 0;
  transition: 0.3s ease;
  &:hover {
    color: ${({ theme }) => theme["@primary"]};
  }
`;

const Error = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  font-size: 1.5em;
  i {
    font-size: 60px;
    margin-bottom: 20px;
    opacity: 0.3;
  }
`;

const Center = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const Companies = ({ loading, companies, error, type, headquarter }) => {
  const { t } = useTranslation(["companies", "global"]);
  const [companiesFetched, setCompaniesFetched] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    localStorage.removeItem("selected_company_id");

    if ((!companies || R.isEmpty(companies)) && !companiesFetched) {
      dispatch(fetchCompanies());
      setCompaniesFetched(true);
    }

    return () => {
      dispatch(FETCH_COMPANIES.clearStatus());
    };
  }, [companies, companiesFetched, dispatch]);

  useEffect(() => {
    const redirectUri = localStorage.getItem("redirect_uri");

    if (companies && redirectUri) {
      const url = new URL(redirectUri);
      const companyId = url.searchParams.get("companyId");
      const company = R.find(R.propEq("id", companyId), companies);

      if (company) {
        dispatch(selectCompany(company, `${url.pathname}${url.search}`));

        localStorage.removeItem("redirect_uri");
      }
    }
  }, [companies, dispatch]);

  useEffect(() => {
    gtag.event({
      category: "Headquarter",
      action: "Headquarter.Company",
      label: headquarter.id
    });
  }, [headquarter]);

  if (!companies && !error) return <SkeletonCompanies />;

  return (
    <Page>
      <Content>
        <Header
          title={t("global:groups")}
          link={
            type === "company"
              ? "/compañias/nueva/company"
              : "/compañias/universityOrcompany"
          }
          label={t("companies:newCompany")}
          actionProps={{
            "data-test": "new-company-btn"
          }}
        />
        <Route
          path="/compañias/universityOrcompany"
          component={UniversityOrCompanyModal}
        />
        <Route path="/compañias/nueva/:type" component={CompanyModal} />

        {loading ? (
          <Center>
            <Spin />
          </Center>
        ) : (
          <Filters data={companies || []} searchProp="name">
            {({ data: companies }) => {
              if (error)
                return (
                  <Error data-test="companies-error">
                    <Icon type="warning" />
                    {t("companies:fetchError")}
                  </Error>
                );

              if (!companies.length)
                return <Placeholder>{t("global:noResults")}</Placeholder>;

              return (
                <Pagination data={R.reverse(companies)} pageSize={20}>
                  {({ data: companies }) => (
                    <List
                      itemLayout="horizontal"
                      dataSource={companies}
                      renderItem={company => {
                        const { logo, name, opportunities = 0 } = company;

                        return (
                          <List.Item
                            data-test="company-item"
                            actions={[
                              <>
                                <Button
                                  key="goToCompanyAnalytics"
                                  icon="line-chart"
                                  type="quaternary"
                                  style={{ marginRight: "10px" }}
                                  onClick={() => {
                                    gtag.event({
                                      category: "Headquarter",
                                      action: "Headquarter.Company.Metrics",
                                      label: company.id
                                    });
                                    dispatch(
                                      selectCompany(company, "/analytics")
                                    );
                                  }}
                                >
                                  {t("global:viewAnalytics")}
                                </Button>
                                <Button
                                  key="goToCompany"
                                  icon="select"
                                  type="quaternary"
                                  onClick={() => {
                                    gtag.event({
                                      category: "Headquarter",
                                      action: "Headquarter.Company.clickButton",
                                      label: company.id
                                    });
                                    dispatch(selectCompany(company));
                                  }}
                                >
                                  {t("companies:select")}
                                </Button>
                              </>
                            ]}
                          >
                            <List.Item.Meta
                              avatar={
                                <Avatar
                                  shape="square"
                                  size="large"
                                  src={httpToHttps(logo)}
                                  icon="shop"
                                />
                              }
                              title={
                                <CompanyName
                                  onClick={() => {
                                    gtag.event({
                                      category: "Headquarter",
                                      action: "Headquarter.Company.clickName",
                                      label: company.id
                                    });
                                    dispatch(selectCompany(company));
                                  }}
                                >
                                  {name}
                                </CompanyName>
                              }
                              description={
                                opportunities === 0
                                  ? t("companies:noOpportunities")
                                  : opportunities === 1
                                  ? `1 ${t("global:opportunity")}`
                                  : `${opportunities} ${t(
                                      "global:opportunities"
                                    )}`
                              }
                            />
                          </List.Item>
                        );
                      }}
                    />
                  )}
                </Pagination>
              );
            }}
          </Filters>
        )}
      </Content>
    </Page>
  );
};
const enhancer = compose(
  connect(state => ({
    headquarter: R.prop("headquarter", state),
    companies: R.prop("companies", state),
    loading: isLoading(FETCH_COMPANIES, state),
    error: FETCH_COMPANIES.getError(state),
    type: R.pathOr("company", ["headquarter", "type"], state)
  }))
);

export default enhancer(Companies);
